// pages/routing/RoutingTabs.jsx — SOUND-826 Phase 1
//
// The six read-only tab bodies for /routing/:companyId. Every value here is a
// CheckoutChamp value stored VERBATIM (mixed types — "9" vs 9, "1" vs 1), so
// nothing renders a raw field without going through utilities/routingView.
// Read-only by design: there are no edit controls here. SOUND-827's propose
// mode lives in RoutingPropose.jsx and reuses the small pieces exported below.
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import RecordTable from "components/Records/RecordTable";
import {
  formatValue,
  formatFlag,
  formatPercent,
  formatMonthlyLimit,
  toFlag,
  resolveMid,
  sortByPriority,
  isGatewayEnabled,
  percentageSummary,
  linkedCascade,
  binRange,
  binMapCardTypes,
  binMapTarget,
  recycleSteps,
  recycleMids,
  cardBrands,
  gatewayFees,
  itemId,
} from "utilities/routingView";

// Adtractive carries ~200 profiles and ~800 BIN maps. Render a page of them and
// let the operator ask for the rest, rather than mounting everything at once.
const PAGE = 50;
const TABLE_PAGE = 200;

export function useShowMore(total, step) {
  const [limit, setLimit] = useState(step);
  return {
    limit,
    more: total > limit ? (
      <div className="flex items-center justify-center gap-3 pt-2 text-sm text-muted-foreground">
        <span>
          Showing {Math.min(limit, total)} of {total}
        </span>
        <button type="button" className="btn btn-ghost" onClick={() => setLimit((l) => l + step)}>
          Show more
        </button>
        <button type="button" className="btn btn-ghost" onClick={() => setLimit(total)}>
          Show all
        </button>
      </div>
    ) : null,
  };
}

/** A MID label: links to the CRM MID when sound-api resolved one. */
export function MidLabel({ mid, empty = "—" }) {
  if (!mid) return <span className="text-muted-foreground">{empty}</span>;
  if (mid.midId) {
    return (
      <span className="min-w-0">
        <Link
          to={`/mids/${mid.midId}`}
          className="font-medium text-foreground hover:text-brand hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          {mid.label}
        </Link>
        <span className="ml-1 text-xs text-muted-foreground">
          {mid.title ? `${mid.title} · ` : ""}#{mid.billerId}
        </span>
      </span>
    );
  }
  return (
    <span className="text-muted-foreground" title="No CRM MID is linked to this CheckoutChamp gateway">
      {mid.label}
    </span>
  );
}

export function EnabledBadge({ value }) {
  const f = toFlag(value);
  if (f === null) return <span className="badge badge-muted">—</span>;
  return f ? (
    <span className="badge badge-success">Enabled</span>
  ) : (
    <span className="badge badge-muted">Disabled</span>
  );
}

export function Fact({ label, children }) {
  return (
    <div className="min-w-0">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div className="mt-0.5 truncate text-sm text-foreground">{children}</div>
    </div>
  );
}

/** A compact table for rows nested inside a card (RecordTable is a card itself). */
export function MiniTable({ headers, children, footer }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <table className="w-full text-[13px] leading-5">
        <thead className="bg-muted/40">
          <tr>
            {headers.map((h) => (
              <th
                key={h.label}
                className={`whitespace-nowrap px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground ${
                  h.align === "right" ? "text-right" : "text-left"
                }`}
              >
                {h.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-border">{children}</tbody>
        {footer && <tfoot className="border-t border-border bg-muted/20">{footer}</tfoot>}
      </table>
    </div>
  );
}

export const td = "px-3 py-1.5 align-middle";

function PercentTotal({ summary, colSpan }) {
  return (
    <tr>
      <td className={`${td} font-medium`}>
        Total (enabled)
      </td>
      <td className={`${td} text-right font-medium`}>
        <span className={summary.flagged ? "text-warning" : ""}>{formatPercent(summary.total)}</span>
      </td>
      <td className={td} colSpan={colSpan}>
        {summary.flagged && (
          <span className="inline-flex items-center gap-1 text-xs text-warning">
            <AlertTriangle className="h-3.5 w-3.5" />
            WEIGHTED set does not sum to 100%
          </span>
        )}
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Profiles
// ---------------------------------------------------------------------------

function ProfileCard({ profile, cascades }) {
  const gateways = sortByPriority(profile.gateways);
  const summary = percentageSummary(profile.gateways, profile.algorithm);
  const cascade = linkedCascade(profile, cascades);

  return (
    <div className="card p-5 space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-base font-semibold text-foreground">{formatValue(profile.title)}</h3>
        <span className="text-xs text-muted-foreground">#{itemId("profiles", profile)}</span>
        <EnabledBadge value={profile.enabled} />
        {summary.flagged && (
          <span className="badge badge-warning">
            <AlertTriangle className="h-3 w-3" />
            Sums to {formatPercent(summary.total)}
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
        <Fact label="Algorithm">{formatValue(profile.algorithm)}</Fact>
        <Fact label="Calc interval">{formatValue(profile.calcInterval)}</Fact>
        <Fact label="Max declines">{formatValue(profile.maxDeclines)}</Fact>
        <Fact label="Max hard declines">{formatValue(profile.maxHardDeclines)}</Fact>
        <Fact label="Limit type">{formatValue(profile.limitType)}</Fact>
        <Fact label="Cascade">
          {cascade
            ? `${cascade.title || "Unknown cascade"} · #${cascade.cascadeId}`
            : "None"}
        </Fact>
      </div>

      {gateways.length === 0 ? (
        <p className="text-sm text-muted-foreground">No gateways in this profile.</p>
      ) : (
        <MiniTable
          headers={[
            { label: "MID" },
            { label: "%", align: "right" },
            { label: "Priority", align: "right" },
            { label: "Enabled" },
            { label: "Limit type" },
            { label: "Sales limit", align: "right" },
            { label: "Profile cap", align: "right" },
          ]}
          footer={<PercentTotal summary={summary} colSpan={5} />}
        >
          {gateways.map((g, i) => (
            <tr
              key={`${g.billerId}-${i}`}
              className={isGatewayEnabled(g) ? "" : "opacity-60"}
            >
              <td className={td}>
                <MidLabel mid={resolveMid(profile, g.billerId, g.title)} />
              </td>
              <td className={`${td} text-right`}>{formatPercent(g.percentage)}</td>
              <td className={`${td} text-right`}>{formatValue(g.priority)}</td>
              <td className={td}>{formatFlag(g.enabled)}</td>
              <td className={td}>{formatValue(g.limitType)}</td>
              <td className={`${td} text-right`}>{formatValue(g.salesLimit)}</td>
              <td className={`${td} text-right`}>{formatValue(g.profileCap)}</td>
            </tr>
          ))}
        </MiniTable>
      )}
    </div>
  );
}

export function ProfilesTab({ items, cascades }) {
  const { limit, more } = useShowMore(items.length, PAGE);
  return (
    <div className="space-y-4">
      {items.slice(0, limit).map((p, i) => (
        <ProfileCard key={itemId("profiles", p) || i} profile={p} cascades={cascades} />
      ))}
      {more}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cascades
// ---------------------------------------------------------------------------

function CascadeCard({ cascade }) {
  const gateways = sortByPriority(cascade.gateways);
  const summary = percentageSummary(cascade.gateways, cascade.algorithm);
  return (
    <div className="card p-5 space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-base font-semibold text-foreground">{formatValue(cascade.title)}</h3>
        <span className="text-xs text-muted-foreground">#{itemId("cascades", cascade)}</span>
        {summary.flagged && (
          <span className="badge badge-warning">
            <AlertTriangle className="h-3 w-3" />
            Sums to {formatPercent(summary.total)}
          </span>
        )}
      </div>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
        <Fact label="Algorithm">{formatValue(cascade.algorithm)}</Fact>
        <Fact label="Calc interval">{formatValue(cascade.calcInterval)}</Fact>
        <Fact label="Cascade attempts">{formatValue(cascade.cascadeAttempt)}</Fact>
      </div>
      {gateways.length === 0 ? (
        <p className="text-sm text-muted-foreground">No gateways in this cascade.</p>
      ) : (
        <MiniTable
          headers={[{ label: "MID" }, { label: "%", align: "right" }, { label: "Priority", align: "right" }]}
          footer={summary.weighted ? <PercentTotal summary={summary} colSpan={1} /> : null}
        >
          {gateways.map((g, i) => (
            <tr key={`${g.billerId}-${i}`} className={isGatewayEnabled(g) ? "" : "opacity-60"}>
              <td className={td}>
                <MidLabel mid={resolveMid(cascade, g.billerId, g.title)} />
              </td>
              <td className={`${td} text-right`}>{formatPercent(g.percentage)}</td>
              <td className={`${td} text-right`}>{formatValue(g.priority)}</td>
            </tr>
          ))}
        </MiniTable>
      )}
    </div>
  );
}

export function CascadesTab({ items }) {
  const { limit, more } = useShowMore(items.length, PAGE);
  return (
    <div className="space-y-4">
      {items.slice(0, limit).map((c, i) => (
        <CascadeCard key={itemId("cascades", c) || i} cascade={c} />
      ))}
      {more}
    </div>
  );
}

// ---------------------------------------------------------------------------
// BIN routing (binMaps)
// ---------------------------------------------------------------------------

export function BinMapsTab({ items, profiles, filtered }) {
  const { limit, more } = useShowMore(items.length, TABLE_PAGE);
  const columns = [
    { key: "name", header: "Name", width: 220, cell: (b) => formatValue(b.name) },
    {
      key: "range",
      header: "BIN range",
      width: 150,
      exportValue: binRange,
      cell: (b) => <span className="font-mono">{binRange(b)}</span>,
    },
    { key: "bankNames", header: "Banks", width: 160, cell: (b) => formatValue(b.bankNames) },
    {
      key: "cardTypes",
      header: "Card types",
      width: 130,
      exportValue: (b) => binMapCardTypes(b).join(", "),
      cell: (b) => {
        const t = binMapCardTypes(b);
        return t.length ? t.join(", ") : <span className="text-muted-foreground">Any</span>;
      },
    },
    { key: "campaignName", header: "Campaign", width: 160, cell: (b) => formatValue(b.campaignName) },
    {
      key: "target",
      header: "Target",
      width: 260,
      title: (b) => {
        const t = binMapTarget(b, profiles);
        return t.kind === "mid" ? t.mid.label : t.kind === "profile" ? t.label : undefined;
      },
      cell: (b) => {
        const t = binMapTarget(b, profiles);
        if (t.kind === "mid") return <MidLabel mid={t.mid} />;
        if (t.kind === "profile") return <span>{t.label}</span>;
        return <span className="text-muted-foreground">—</span>;
      },
    },
    { key: "enabled", header: "Enabled", width: 100, cell: (b) => <EnabledBadge value={b.enabled} /> },
  ];
  return (
    <div className="space-y-2">
      <RecordTable
        columns={columns}
        rows={items.slice(0, limit)}
        rowKey={(b, i) => itemId("binMaps", b) || i}
        filtered={filtered}
        empty={{ heading: "No BIN routing rules" }}
        noMatch={{ heading: "No matching BIN routing rules", description: "Search by name, BIN, bank, campaign or MID." }}
      />
      {more}
    </div>
  );
}

// ---------------------------------------------------------------------------
// BIN blocks (binProfiles)
// ---------------------------------------------------------------------------

export function BinProfilesTab({ items, filtered }) {
  const { limit, more } = useShowMore(items.length, TABLE_PAGE);
  const columns = [
    { key: "id", header: "ID", width: 80, cell: (b) => itemId("binProfiles", b) || "—" },
    {
      key: "range",
      header: "BIN range",
      width: 180,
      exportValue: binRange,
      cell: (b) => <span className="font-mono">{binRange(b)}</span>,
    },
    { key: "blockMessage", header: "Block message", width: 600, cell: (b) => formatValue(b.blockMessage) },
  ];
  return (
    <div className="space-y-2">
      <RecordTable
        columns={columns}
        rows={items.slice(0, limit)}
        rowKey={(b, i) => itemId("binProfiles", b) || i}
        filtered={filtered}
        empty={{ heading: "No BIN blocks" }}
        noMatch={{ heading: "No matching BIN blocks", description: "Search by BIN or message." }}
      />
      {more}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Recycle billing
// ---------------------------------------------------------------------------

function RecycleCard({ item }) {
  const mids = recycleMids(item);
  const steps = recycleSteps(item);
  return (
    <div className="card p-5 space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-base font-semibold text-foreground">
          Recycle profile #{itemId("recycle", item) || "—"}
        </h3>
        <span className="badge badge-muted">{formatValue(item.orderType || "BOTH")}</span>
      </div>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <Fact label="Hold price">{formatFlag(item.holdPrice)}</Fact>
        <Fact label="Flat amount">{formatFlag(item.useFlatAmount)}</Fact>
        <Fact label="Attempt Friday">{formatFlag(item.attemptFriday)}</Fact>
        <Fact label="Apply to all">{formatFlag(item.applyToAll)}</Fact>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-1">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Attached MIDs
          </div>
          {mids.attached.length ? (
            <ul className="space-y-1 text-sm">
              {mids.attached.map((m) => (
                <li key={m.billerId}>
                  <MidLabel mid={m} />
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">None</p>
          )}
        </div>
        <div className="space-y-1 text-sm">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Attempt MIDs
          </div>
          <div className="grid grid-cols-[110px_1fr] gap-x-3 gap-y-1">
            <span className="text-muted-foreground">Attempts 4–6</span>
            <MidLabel mid={mids.attempts4to6} empty="Same MID" />
            <span className="text-muted-foreground">Attempts 7–9</span>
            <MidLabel mid={mids.attempts7to9} empty="Same MID" />
            <span className="text-muted-foreground">Final</span>
            <MidLabel mid={mids.final} empty="Same MID" />
          </div>
        </div>
      </div>

      <MiniTable
        headers={[
          { label: "Step", align: "right" },
          { label: "Interval (days)", align: "right" },
          { label: "Price reduction", align: "right" },
        ]}
      >
        {steps.map((s) => (
          <tr key={s.step} className={s.active ? "" : "opacity-50"}>
            <td className={`${td} text-right`}>{s.step}</td>
            <td className={`${td} text-right`}>{formatValue(s.intervalDays)}</td>
            <td className={`${td} text-right`}>{formatValue(s.priceReduction)}</td>
          </tr>
        ))}
      </MiniTable>
    </div>
  );
}

export function RecycleTab({ items }) {
  const { limit, more } = useShowMore(items.length, PAGE);
  return (
    <div className="space-y-4">
      {items.slice(0, limit).map((r, i) => (
        <RecycleCard key={itemId("recycle", r) || i} item={r} />
      ))}
      {more}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Gateways
// ---------------------------------------------------------------------------

export function GatewaysTab({ items, filtered }) {
  const { limit, more } = useShowMore(items.length, TABLE_PAGE);
  const columns = [
    { key: "billerId", header: "Biller ID", width: 90, cell: (g) => formatValue(g.billerId) },
    { key: "title", header: "Title", width: 200, cell: (g) => formatValue(g.title) },
    {
      key: "mid",
      header: "CRM MID",
      width: 220,
      title: (g) => resolveMid(g, g.billerId, null)?.label,
      cell: (g) => {
        const mid = resolveMid(g, g.billerId, null);
        return mid?.midId ? <MidLabel mid={mid} /> : <span className="text-muted-foreground">Not linked</span>;
      },
    },
    { key: "enabled", header: "Enabled", width: 100, cell: (g) => <EnabledBadge value={g.enabled} /> },
    { key: "gatewayName", header: "Gateway", width: 150, cell: (g) => formatValue(g.gatewayName) },
    { key: "descriptor", header: "Descriptor", width: 160, cell: (g) => formatValue(g.descriptor) },
    { key: "midNumber", header: "MID number", width: 150, cell: (g) => formatValue(g.midNumber) },
    {
      key: "brands",
      header: "Card brands",
      width: 190,
      exportValue: (g) => cardBrands(g).join(", "),
      cell: (g) => cardBrands(g).join(", ") || <span className="text-muted-foreground">None</span>,
    },
    {
      key: "monthlyLimit",
      header: "Monthly limit",
      width: 130,
      align: "right",
      exportValue: (g) => formatMonthlyLimit(g.monthlyLimit),
      cell: (g) => formatMonthlyLimit(g.monthlyLimit),
    },
    {
      key: "fees",
      header: "Fees / rates",
      width: 240,
      exportValue: (g) =>
        gatewayFees(g)
          .map((f) => `${f.label} ${f.value}`)
          .join(" · "),
      cell: (g) => {
        const fees = gatewayFees(g);
        return fees.length ? (
          fees.map((f) => `${f.label} ${f.value}`).join(" · ")
        ) : (
          <span className="text-muted-foreground">None set</span>
        );
      },
    },
  ];
  return (
    <div className="space-y-2">
      <RecordTable
        columns={columns}
        rows={items.slice(0, limit)}
        rowKey={(g, i) => itemId("gateways", g) || i}
        filtered={filtered}
        empty={{ heading: "No gateways" }}
        noMatch={{ heading: "No matching gateways", description: "Search by biller ID, title, descriptor or MID." }}
      />
      {more}
    </div>
  );
}
